import { useState } from "react";
import GenerateReport from "./GenerateReport";

// ═══════════════════════════════════════════════════════
// MATRICE DE SCORING — QUESTIONNAIRE C — 12-14 ANS
// ═══════════════════════════════════════════════════════


const PROFILES = {
  INT: {
    name: "L'Athlète",
    emoji: "⚡",
    tagline: "L'effort physique intense est sa source d'énergie",
    description: "Cet adolescent se réalise par le dépassement physique. Il a besoin de transpirer, de se surpasser, de sentir son corps à la limite. Les activités sans effort intense lui semblent vides de sens.",
    color: "#E8732A", bg: "#FFF0E6",
    top: [
      { emoji: "🏊", name: "Natation compétition", why: "Effort pur, progression mesurable au chrono, discipline personnelle" },
      { emoji: "🏃", name: "Athlétisme", why: "Se dépasser soi-même, résultats immédiats, variété des disciplines" },
      { emoji: "🥊", name: "Sports de combat intensifs", why: "Effort total, dépassement physique et mental" },
      { emoji: "🧗", name: "Escalade compétition", why: "Effort complet, progression visible, défis physiques constants" },
    ],
    explore: ["Triathlon junior", "Rugby", "Basketball compétition", "CrossFit adapté ados"],
    avoid: "Les activités à faible intensité physique ou trop artistiques — gym rythmique douce, musique classique, théâtre.",
  },
  PRE: {
    name: "Le Maître Technicien",
    emoji: "🎯",
    tagline: "La maîtrise parfaite du geste est son obsession",
    description: "Cet adolescent est animé par la perfection technique. Il veut comprendre chaque détail, progresser de façon mesurable, sentir qu'il contrôle chaque aspect de sa discipline. La répétition purposeful le satisfait profondément.",
    color: "#3D2B8E", bg: "#EDE8FF",
    top: [
      { emoji: "⚔️", name: "Escrime compétition", why: "Précision absolue, stratégie, classements nationaux accessibles" },
      { emoji: "🎾", name: "Tennis classement", why: "Technique complexe, duel individuel, progression par le classement" },
      { emoji: "🏹", name: "Tir à l'arc compétition", why: "Concentration maximale, geste parfait, résultats mesurables" },
      { emoji: "🥋", name: "Arts martiaux — ceintures noires", why: "Maîtrise technique, grades progressifs, discipline de vie" },
    ],
    explore: ["Golf compétition", "Équitation dressage", "Piano ou violon niveau avancé", "Natation technique"],
    avoid: "Les activités trop collectives où la technique individuelle est diluée, ou les sports sans objectif mesurable.",
  },
  EXP: {
    name: "L'Artiste en devenir",
    emoji: "🎨",
    tagline: "Créer et s'exprimer est une nécessité, pas un loisir",
    description: "Cet adolescent a besoin d'un espace d'expression totale. Les activités qui lui permettent de mettre sa patte, d'inventer, de créer quelque chose d'unique lui sont vitales. La liberté créative n'est pas négociable.",
    color: "#9B4FBB", bg: "#F5E8FF",
    top: [
      { emoji: "💃", name: "Danse contemporaine ou hip-hop", why: "Expression corporelle totale, liberté créative, créations personnelles" },
      { emoji: "🎵", name: "Musique — composition et pratique avancée", why: "Créer ses propres morceaux, s'exprimer, se produire" },
      { emoji: "🎭", name: "Théâtre — textes et mise en scène", why: "Expression totale, liberté d'interprétation, représentations" },
      { emoji: "🎪", name: "Arts du cirque avancés", why: "Corps et créativité, numéros personnels, spectacle" },
    ],
    explore: ["Capoeira", "Wushu / arts martiaux artistiques", "Street art / graffiti légal", "Photographie artistique"],
    avoid: "Les activités très codifiées où chaque geste est imposé — gym rythmique stricte, athlétisme pur, natation technique sans aspect artistique.",
  },
  COM: {
    name: "Le Compétiteur né",
    emoji: "⚔️",
    tagline: "La compétition ne le stresse pas — elle le révèle",
    description: "Cet adolescent se transcende face à un adversaire. Il a besoin d'un enjeu réel, d'un classement, d'une victoire à conquérir. La compétition est pour lui un carburant, pas une source de stress.",
    color: "#C62828", bg: "#FFEBEE",
    top: [
      { emoji: "🥊", name: "Sports de combat en compétition", why: "Duel pur, montée en puissance progressive, championnats" },
      { emoji: "♟️", name: "Échecs en tournois", why: "Compétition mentale pure, classement ELO, stratégie" },
      { emoji: "⚔️", name: "Escrime compétition nationale", why: "Duel élégant, classements, championnats régionaux et nationaux" },
      { emoji: "🎾", name: "Tennis classement FFT", why: "Duel direct, classement officiel, progression mesurable" },
    ],
    explore: ["E-sport compétition", "Natation compétition", "Athlétisme chrono", "Badminton compétition"],
    avoid: "Les activités sans enjeu compétitif clair, les sports collectifs où la responsabilité individuelle est noyée.",
  },
  COL: {
    name: "L'Âme collective",
    emoji: "🤝",
    tagline: "Le groupe est son moteur — seul il s'éteint",
    description: "Cet adolescent s'épanouit dans la dynamique collective. Les liens forts, les victoires partagées, les défaites assumées ensemble — c'est ce qui lui donne de l'énergie. L'ambiance compte autant que la performance.",
    color: "#1B6B3A", bg: "#E8F5E9",
    top: [
      { emoji: "⚽", name: "Football club compétition", why: "Collectif soudé, victoires et défaites partagées, cohésion forte" },
      { emoji: "🏀", name: "Basketball compétition", why: "Interdépendance maximale, esprit d'équipe, jeu rapide" },
      { emoji: "🎭", name: "Troupe de théâtre", why: "Création collective, liens humains forts, spectacles partagés" },
      { emoji: "🎵", name: "Groupe de musique ou orchestre", why: "Harmonie collective, appartenance forte, concerts ensemble" },
    ],
    explore: ["Handball", "Volleyball", "Comédie musicale", "Aviron en équipe"],
    avoid: "Les activités strictement individuelles — tir à l'arc, natation individuelle, échecs, escrime solo.",
  },
  SCE: {
    name: "Le Performer",
    emoji: "🎭",
    tagline: "Le public n'est pas un stress — c'est son carburant",
    description: "Cet adolescent donne son meilleur devant un public. Être regardé l'énergise, le transcende. Il a besoin de représentations régulières pour maintenir sa motivation. Les coulisses le frustrent — c'est la scène qui le révèle.",
    color: "#1565C0", bg: "#E3F2FD",
    top: [
      { emoji: "🎭", name: "Théâtre — rôles principaux", why: "Performance pure, public, représentations régulières" },
      { emoji: "💃", name: "Danse de scène compétition", why: "Public, spectacles, battles — être regardé et applaudi" },
      { emoji: "🎵", name: "Musique — concerts et récitals", why: "Se produire devant les autres, maîtrise technique visible" },
      { emoji: "🎪", name: "Arts du cirque — numéros solo", why: "Performance devant public, numéros personnels, show total" },
    ],
    explore: ["Comédie musicale", "Danse hip-hop battles", "Magie et prestidigitation", "Improvisation théâtrale"],
    avoid: "Les activités sans représentation publique — natation, athlétisme sans public, arts martiaux sans tournois ouverts.",
  },
};


// ── QUESTIONS ADO (12-14 ans) ──
const ADO_QUESTIONS = [
  {
    id: "A1",
    text: "Si tu pouvais inventer ton activité idéale, elle ressemblerait à quoi ?",
    consigne: "Choisis les 2 réponses les plus proches de ton idéal",
    multi: true, max: 2,
    options: [
      { id: "A", text: "⚡  Intense et physique — me dépenser, transpirer, me dépasser", scores: { INT: 3 } },
      { id: "B", text: "🎯  Technique et précise — maîtriser des gestes complexes, progresser", scores: { PRE: 3 } },
      { id: "C", text: "🎨  Créative et libre — m'exprimer, inventer, créer quelque chose d'unique", scores: { EXP: 3 } },
      { id: "D", text: "🏆  Compétitive — me mesurer aux autres, gagner, me classer", scores: { COM: 3 } },
      { id: "E", text: "🤝  Collective — faire partie d'un groupe soudé, partager", scores: { COL: 3 } },
      { id: "F", text: "🎭  Performative — me produire devant un public, être regardé", textF: "🎭  Performative — me produire devant un public, être regardée", scores: { SCE: 3 } },
    ],
  },
  {
    id: "A2",
    text: "Qu'est-ce qui te motive le plus dans une activité ?",
    consigne: "Choisis les 2 réponses les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Progresser et voir mes propres améliorations — peu importe le classement", scores: { PRE: 2 } },
      { id: "B", text: "Gagner et être reconnu — la compétition m'anime vraiment", textF: "Gagner et être reconnue — la compétition m'anime vraiment", scores: { COM: 3 } },
      { id: "C", text: "M'exprimer et créer quelque chose — le processus créatif prime", scores: { EXP: 3 } },
      { id: "D", text: "Appartenir à un groupe — l'ambiance et les liens comptent plus que la performance", scores: { COL: 3 } },
      { id: "E", text: "Maîtriser parfaitement une technique — j'aime contrôler chaque détail", scores: { PRE: 3 } },
    ],
  },
  {
    id: "A3",
    text: "Comment tu vis la compétition ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Elle me galvanise — je me dépasse quand il y a un enjeu réel", scores: { COM: 3 } },
      { id: "B", text: "Je l'apprécie modérément — j'aime me mesurer mais la pression me pèse parfois", scores: { COM: 1 } },
      { id: "C", text: "Elle me stresse — je préfère nettement les activités sans classement", scores: { COL: 1, EXP: 1 } },
      { id: "D", text: "Elle ne m'intéresse pas — ce que les autres font ne m'affecte pas", scores: { PRE: 1 } },
    ],
  },
  {
    id: "A4",
    text: "Qu'est-ce qui t'a fait abandonner ou presque abandonner une activité ?",
    consigne: "Choisis les 2 raisons les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "La répétition sans fin — ça m'ennuyait profondément", scores: { EXP: 1, INT: 1 } },
      { id: "B", text: "La pression compétitive ou le regard des juges — trop de stress", scores: { COL: 1, EXP: 1 } },
      { id: "C", text: "Le manque de liberté créative — il fallait faire exactement comme le prof", scores: { EXP: 2 } },
      { id: "D", text: "Les résultats trop lents à venir — j'avais besoin de progresser vite", scores: { INT: 1, COM: 1 } },
      { id: "E", text: "L'ambiance ou les autres participants ne me correspondaient pas", scores: { COL: 1 } },
      { id: "F", text: "Je n'ai jamais vraiment abandonné une activité", scores: { INT: 1 } },
    ],
  },
  {
    id: "A5",
    text: "Dans quel type de groupe tu te sens le mieux ?",
    consigne: "Choisis les 2 réponses les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Seul — je progresse mieux et je suis plus concentré sans les autres", textF: "Seule — je progresse mieux et je suis plus concentrée sans les autres", scores: { PRE: 2 } },
      { id: "B", text: "En duo ou petit groupe de 2-3 personnes — les liens forts comptent plus", scores: { COL: 1 } },
      { id: "C", text: "Dans une grande équipe avec un vrai esprit collectif", scores: { COL: 3 } },
      { id: "D", text: "Devant un public — je m'élève quand des gens me regardent", scores: { SCE: 3 } },
    ],
  },
  {
    id: "A6",
    text: "Quand tu es dans ta \"zone\" — complètement absorbé — qu'est-ce que tu ressens ?",
    consigne: "Choisis les 2 réponses les plus proches de ce que tu vis",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Une sensation physique intense — effort, vitesse, puissance dans le corps", scores: { INT: 3 } },
      { id: "B", text: "Une précision mentale totale — chaque geste calculé et maîtrisé", scores: { PRE: 3 } },
      { id: "C", text: "Une liberté d'expression — je crée, j'invente, je m'exprime sans contrainte", scores: { EXP: 3 } },
      { id: "D", text: "Une connexion avec le groupe — on fonctionne ensemble comme une machine", scores: { COL: 3 } },
      { id: "E", text: "Un silence intérieur — plus aucune pensée parasite, juste le moment présent", scores: { PRE: 2 } },
    ],
  },
  {
    id: "A7",
    text: "Ton rapport à l'effort physique ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "J'adore l'effort intense — transpirer, me dépasser physiquement me fait du bien", scores: { INT: 3 } },
      { id: "B", text: "J'apprécie l'effort technique — la précision plus que l'intensité", scores: { PRE: 3 } },
      { id: "C", text: "J'aime l'effort expressif — bouger avec fluidité et grâce plutôt que la force brute", scores: { EXP: 3 } },
      { id: "D", text: "Je préfère les activités où l'effort physique est secondaire — créativité ou réflexion", scores: { EXP: 2 } },
    ],
  },
  {
    id: "A8",
    text: "Ce qui compte le plus pour toi dans une activité — tes 2 priorités",
    consigne: "Choisis les 2 réponses les plus importantes pour toi",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Le plaisir et la sensation dans le moment", scores: { INT: 1, EXP: 1 } },
      { id: "B", text: "La progression et la maîtrise technique", scores: { PRE: 2 } },
      { id: "C", text: "La reconnaissance et les résultats visibles", scores: { COM: 2 } },
      { id: "D", text: "Les liens humains et l'ambiance", scores: { COL: 2 } },
    ],
  },
  {
    id: "A9",
    text: "Comment tu apprends le mieux dans une nouvelle activité ?",
    consigne: "Choisis les 2 réponses les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "En essayant directement — j'apprends par l'expérience, pas par les explications", scores: { INT: 2 } },
      { id: "B", text: "En observant d'abord les autres, puis en reproduisant", scores: { PRE: 1 } },
      { id: "C", text: "En comprenant la logique et la technique en détail avant de pratiquer", scores: { PRE: 2 } },
      { id: "D", text: "En inventant ma propre façon de faire — je n'aime pas copier le modèle", scores: { EXP: 2 } },
    ],
  },
  {
    id: "A10",
    text: "Ton rapport à la scène, au public, à être regardé ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "J'adore — ça me stimule, je me surpasse devant les gens", scores: { SCE: 3 } },
      { id: "B", text: "J'aime bien — c'est agréable mais pas ce qui me motive le plus", scores: { SCE: 1 } },
      { id: "C", text: "Je le fais si nécessaire mais je préfèrerais m'en passer", scores: { PRE: 1 } },
      { id: "D", text: "Je déteste — être regardé me paralyse ou me stresse profondément", textF: "Je déteste — être regardée me paralyse ou me stresse profondément", scores: { PRE: 2 } },
    ],
  },
  {
    id: "A11",
    text: "Qu'est-ce qui t'ennuie ou t'irrite le plus rapidement ?",
    consigne: "Choisis les 2 réponses les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Répéter les mêmes exercices sans voir la progression", scores: { EXP: 1, INT: 1 } },
      { id: "B", text: "Les consignes trop strictes qui ne laissent aucune place à l'initiative", scores: { EXP: 2 } },
      { id: "C", text: "Les conflits d'ego ou la mauvaise ambiance dans le groupe", scores: { COL: 1 } },
      { id: "D", text: "Le manque de résultats visibles rapidement", scores: { COM: 1, INT: 1 } },
      { id: "E", text: "Les entraîneurs ou professeurs trop autoritaires", scores: { EXP: 1 } },
    ],
  },
  {
    id: "A12",
    text: "En dehors de toute activité organisée, que fais-tu spontanément ?",
    consigne: "Choisis les 2 réponses les plus fréquentes",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Je bouge — sport seul, je cours, je joue dehors sans organisation", textF: "Je bouge — sport seule, je cours, je joue dehors sans organisation", scores: { INT: 2 } },
      { id: "B", text: "Je crée — dessine, fabrique, écris, compose, invente quelque chose", scores: { EXP: 2 } },
      { id: "C", text: "J'écoute de la musique ou j'en joue — la musique fait partie de ma bulle", scores: { EXP: 2 } },
      { id: "D", text: "Je passe du temps avec mes amis — les liens sociaux sont ma priorité", scores: { COL: 2 } },
      { id: "E", text: "Je regarde des vidéos sur des activités qui m'intéressent", scores: { SCE: 1 } },
    ],
  },
  {
    id: "A13",
    text: "Dans 5 ans, comment tu imagines ta vie par rapport au sport ou aux arts ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Je veux pratiquer à haut niveau — la compétition ou la performance sérieuse m'attire", scores: { COM: 2 } },
      { id: "B", text: "Je veux avoir une vraie passion qui m'accompagne pour la vie, sans pression", scores: { PRE: 1, EXP: 1 } },
      { id: "C", text: "Je veux pouvoir pratiquer avec des amis, pour le plaisir et le partage", scores: { COL: 2 } },
      { id: "D", text: "Je ne sais pas encore — rien ne m'a vraiment accroché jusqu'ici", scores: {} },
    ],
  },
  {
    id: "A14",
    text: "La phrase qui te décrit le mieux dans une activité ?",
    consigne: "Choisis les 2 phrases les plus vraies",
    multi: true, max: 2,
    options: [
      { id: "A", text: "\"Je préfère pratiquer seul — les autres me ralentissent\"", textF: "\"Je préfère pratiquer seule — les autres me ralentissent\"", scores: { PRE: 2 } },
      { id: "B", text: "\"Je m'allume quand il y a un enjeu — la pression me stimule\"", scores: { COM: 2, SCE: 1 } },
      { id: "C", text: "\"J'ai besoin de liberté — les cadres trop rigides m'étouffent\"", scores: { EXP: 2 } },
      { id: "D", text: "\"Je veux maîtriser — la précision et le détail me passionnent\"", scores: { PRE: 2 } },
      { id: "E", text: "\"Je veux que ça serve à quelque chose — créer, laisser une trace\"", scores: { EXP: 1, PRE: 1 } },
      { id: "F", text: "\"Le groupe et l'ambiance comptent autant que l'activité\"", scores: { COL: 2 } },
    ],
  },
  {
    id: "A15",
    text: "Ton rapport au public et à être regardé dans ta pratique ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Le public me transcende — c'est là que je donne le meilleur", scores: { SCE: 3 } },
      { id: "B", text: "J'aime les représentations mais elles ne changent pas ma façon de pratiquer", scores: { SCE: 1 } },
      { id: "C", text: "Je préfère pratiquer sans public — le regard des autres me déconcentre", scores: { PRE: 1 } },
      { id: "D", text: "L'idée du public me stresse — je fonctionne mieux dans l'anonymat", scores: { PRE: 2 } },
    ],
  },
];

// ── QUESTIONS PARENT (5 questions) ──
const PARENT_QUESTIONS = [
  {
    id: "P1",
    text: "Y a-t-il une activité où vous avez vu votre enfant s'allumer d'une façon particulière ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Oui — une activité artistique ou créative", scores: { EXP: 2 } },
      { id: "B", text: "Oui — une activité physique intense ou sportive", scores: { INT: 2 } },
      { id: "C", text: "Oui — une activité compétitive (tournoi, classement)", scores: { COM: 2 } },
      { id: "D", text: "Oui — une activité devant un public ou en représentation", scores: { SCE: 2 } },
      { id: "E", text: "Oui — une activité collective avec des liens forts", scores: { COL: 2 } },
      { id: "F", text: "Non, je n'ai pas observé de moment comme ça", scores: {} },
    ],
  },
  {
    id: "P2",
    text: "Si votre enfant a abandonné une activité, qu'est-ce qui l'a fait décrocher ?",
    consigne: "Une seule réponse",
    multi: false, max: 1,
    options: [
      { id: "A", text: "La pression ou le regard de l'entraîneur ou des autres parents", scores: { COL: 1 } },
      { id: "B", text: "La lenteur des progrès — il s'impatientait", textF: "La lenteur des progrès — elle s'impatientait", scores: { INT: 1, COM: 1 } },
      { id: "C", text: "Les conflits avec d'autres enfants ou une mauvaise ambiance", scores: { COL: 1 } },
      { id: "D", text: "Il ne s'est jamais vraiment reconnu dans l'activité", textF: "Elle ne s'est jamais vraiment reconnue dans l'activité", scores: {} },
      { id: "E", text: "Votre enfant n'a jamais abandonné une activité", scores: { INT: 1 } },
    ],
  },
  {
    id: "P3",
    text: "Que fait votre enfant spontanément quand il est libre et désoeuvré ?",
    consigne: "Cochez les 2 réponses les plus fréquentes",
    multi: true, max: 2,
    options: [
      { id: "A", text: "Il bouge, sort, fait du sport seul", textF: "Elle bouge, sort, fait du sport seule", scores: { INT: 2 } },
      { id: "B", text: "Il crée — dessine, écrit, fabrique, compose", textF: "Elle crée — dessine, écrit, fabrique, compose", scores: { EXP: 2 } },
      { id: "C", text: "Il écoute de la musique ou joue d'un instrument", textF: "Elle écoute de la musique ou joue d'un instrument", scores: { EXP: 2 } },
      { id: "D", text: "Il recherche des amis ou de la compagnie", textF: "Elle recherche des amis ou de la compagnie", scores: { COL: 2 } },
      { id: "E", text: "Il regarde des vidéos ou joue en ligne", textF: "Elle regarde des vidéos ou joue en ligne", scores: { SCE: 1 } },
    ],
  },
  {
    id: "P4",
    text: "Y a-t-il un talent de votre enfant que vous pensez sous-exploité ou qu'il ne reconnaît pas encore ?",
    consigne: "Cochez la réponse la plus proche",
    multi: false, max: 1,
    options: [
      { id: "A", text: "Un talent artistique ou créatif (musique, danse, dessin, écriture...)", scores: { EXP: 2 } },
      { id: "B", text: "Un talent athlétique ou physique (vitesse, coordination, endurance...)", scores: { INT: 2 } },
      { id: "C", text: "Un talent de leader ou de meneur dans un groupe", scores: { COM: 1, COL: 1 } },
      { id: "D", text: "Un talent de performer — il est différent quand les autres le regardent", textF: "Un talent de performer — elle est différente quand les autres la regardent", scores: { SCE: 2 } },
      { id: "E", text: "Un talent technique ou de précision — il fait les choses avec soin", textF: "Un talent technique ou de précision — elle fait les choses avec soin", scores: { PRE: 2 } },
      { id: "F", text: "Je ne vois pas de talent particulièrement sous-exploité", scores: {} },
    ],
  },
  {
    id: "P5",
    text: "En une phrase, comment décririez-vous votre enfant dans une activité ?",
    consigne: "Cochez la phrase la plus vraie",
    multi: false, max: 1,
    options: [
      { id: "A", text: `“Il se dépense sans compter — il n'a pas de demi-mesure”`, textF: `“Elle se dépense sans compter — elle n'a pas de demi-mesure”`, scores: { INT: 2 } },
      { id: "B", text: `“Il est dans sa bulle créative — il peut pratiquer des heures seul”`, textF: `“Elle est dans sa bulle créative — elle peut pratiquer des heures seule”`, scores: { EXP: 2, PRE: 1 } },
      { id: "C", text: `“Il a besoin du groupe — il s'éteint quand il pratique seul”`, textF: `“Elle a besoin du groupe — elle s'éteint quand elle pratique seule”`, scores: { COL: 2 } },
      { id: "D", text: `“Il veut gagner — la compétition le révèle complètement”`, textF: `“Elle veut gagner — la compétition la révèle complètement”`, scores: { COM: 2 } },
      { id: "E", text: `“Il est différent devant un public — il s'élève quand on le regarde”`, textF: `“Elle est différente devant un public — elle s'élève quand on la regarde”`, scores: { SCE: 2 } },
      { id: "F", text: `“Il est perfectionniste — il ne supporte pas de mal faire”`, textF: `“Elle est perfectionniste — elle ne supporte pas de mal faire”`, scores: { PRE: 2 } },
    ],
  },
];

// ── CALCUL SCORES ──
function computeScores(adoAnswers, parentAnswers) {
  const sc = { INT: 0, PRE: 0, EXP: 0, COM: 0, COL: 0, SCE: 0 };
  ADO_QUESTIONS.forEach(q => {
    const ans = adoAnswers[q.id] || [];
    ans.forEach(sel => {
      const o = q.options.find(x => x.id === sel);
      if (o) Object.entries(o.scores).forEach(([k, v]) => { sc[k] += v; });
    });
  });
  PARENT_QUESTIONS.forEach(q => {
    const ans = parentAnswers[q.id] || [];
    ans.forEach(sel => {
      const o = q.options.find(x => x.id === sel);
      if (o) Object.entries(o.scores).forEach(([k, v]) => { sc[k] += v; });
    });
  });
  const total = Object.values(sc).reduce((a, b) => a + b, 0) || 1;
  const pct = {};
  Object.keys(sc).forEach(k => { pct[k] = Math.round((sc[k] / total) * 100); });
  const sorted = Object.entries(sc).sort((a, b) => b[1] - a[1]);
  return { sc, pct, primary: sorted[0][0], secondary: sorted[1][0] };
}

// ── COMPOSANT PRINCIPAL ──
function applyGenderQ(text, gender) {
  if (!gender) return text;
  const isF = gender === "Fille";
  const map = {
    "Ton rapport \u00e0 la sc\u00e8ne, au public, \u00e0 \u00eatre regard\u00e9 ?": { G: "Ton rapport \u00e0 la sc\u00e8ne, au public, \u00e0 \u00eatre regard\u00e9 ?", F: "Ton rapport \u00e0 la sc\u00e8ne, au public, \u00e0 \u00eatre regard\u00e9e ?" },
    "Ton rapport au public et \u00e0 \u00eatre regard\u00e9 dans ta pratique ?": { G: "Ton rapport au public et \u00e0 \u00eatre regard\u00e9 dans ta pratique ?", F: "Ton rapport au public et \u00e0 \u00eatre regard\u00e9e dans ta pratique ?" },
    "Y a-t-il une activit\u00e9 o\u00f9 vous avez vu votre enfant s'allumer d'une fa\u00e7on particuli\u00e8re ?": { G: "Y a-t-il une activit\u00e9 o\u00f9 vous avez vu votre fils s'animer d'une fa\u00e7on particuli\u00e8re ?", F: "Y a-t-il une activit\u00e9 o\u00f9 vous avez vu votre fille s'animer d'une fa\u00e7on particuli\u00e8re ?" },
    "Si votre enfant a abandonn\u00e9 une activit\u00e9, qu'est-ce qui l'a fait d\u00e9crocher ?": { G: "Si votre fils a abandonn\u00e9 une activit\u00e9, qu'est-ce qui l'a fait d\u00e9crocher ?", F: "Si votre fille a abandonn\u00e9 une activit\u00e9, qu'est-ce qui l'a fait d\u00e9crocher ?" },
    "Que fait votre enfant spontan\u00e9ment quand il est libre et d\u00e9soeuvr\u00e9 ?": { G: "Que fait votre fils spontan\u00e9ment quand il est libre ?", F: "Que fait votre fille spontan\u00e9ment quand elle est libre ?" },
    "Y a-t-il un talent de votre enfant que vous pensez sous-exploit\u00e9 ou qu'il ne reconna\u00eet pas encore ?": { G: "Y a-t-il un talent de votre fils que vous pensez sous-exploit\u00e9 ou qu'il ne reconna\u00eet pas encore ?", F: "Y a-t-il un talent de votre fille que vous pensez sous-exploit\u00e9 ou qu'elle ne reconna\u00eet pas encore ?" },
    "En une phrase, comment d\u00e9cririez-vous votre enfant dans une activit\u00e9 ?": { G: "En une phrase, comment d\u00e9cririez-vous votre fils dans une activit\u00e9 ?", F: "En une phrase, comment d\u00e9cririez-vous votre fille dans une activit\u00e9 ?" },
  };
  if (map[text]) return isF ? map[text].F : map[text].G;
  return text;
}

export default function MatriceC({ onBack }) {
  const [step, setStep] = useState(0);
  const [aq, setAq] = useState(0);
  const [pq, setPq] = useState(0);
  const [adoAns, setAdoAns] = useState({});
  const [parAns, setParAns] = useState({});
  const [name, setName] = useState("");
  const [gender, setGender] = useState(""); // eslint-disable-line
  const [age, setAge] = useState("");
  const [res, setRes] = useState(null);

  function ansAdo(qId, oId, multi, max) {
    setAdoAns(p => {
      const cur = p[qId] || [];
      if (!multi || max === 1) return { ...p, [qId]: [oId] };
      if (cur.includes(oId)) return { ...p, [qId]: cur.filter(x => x !== oId) };
      if (cur.length >= max) return { ...p, [qId]: [...cur.slice(1), oId] };
      return { ...p, [qId]: [...cur, oId] };
    });
  }

  function ansPar(qId, oId, multi, max) {
    setParAns(p => {
      const cur = p[qId] || [];
      if (!multi || max === 1) return { ...p, [qId]: [oId] };
      if (cur.includes(oId)) return { ...p, [qId]: cur.filter(x => x !== oId) };
      if (cur.length >= max) return { ...p, [qId]: [...cur.slice(1), oId] };
      return { ...p, [qId]: [...cur, oId] };
    });
  }

  function nextA() { if (aq < ADO_QUESTIONS.length - 1) setAq(aq + 1); else setStep(2); }
  function prevA() { if (aq > 0) setAq(aq - 1); else setStep(0); }
  function nextP() {
    if (pq < PARENT_QUESTIONS.length - 1) setPq(pq + 1);
    else { setRes(computeScores(adoAns, parAns)); setStep(3); }
  }
  function prevP() { if (pq > 0) setPq(pq - 1); else setStep(1); }

  const V = "#1B5E7D", VM = "#2E86AB", VP = "#D4EAF5", G = "#E8C87A";
  const btn = (c = V) => ({ background: c, color: "#fff", border: "none", borderRadius: 100, padding: "12px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer" });
  const bto = { background: "transparent", color: V, border: `2px solid ${VP}`, borderRadius: 100, padding: "10px 24px", fontSize: 14, cursor: "pointer" };
  const card = { background: "#fff", borderRadius: 20, padding: "28px 24px", maxWidth: 600, margin: "0 auto", boxShadow: "0 4px 24px rgba(27,94,125,0.08)" };
  const sm = { fontFamily: "system-ui", fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#4A9BC4", textTransform: "uppercase" };

  // INTRO
  if (step === 0) return (
    <div style={{ background: "#F0F8FF", minHeight: "100vh", fontFamily: "system-ui" }}>
      <div style={{ background: `linear-gradient(135deg, ${V}, ${VM})`, padding: "28px 20px 24px", borderRadius: "0 0 24px 24px" }}>
        <div style={{ ...sm, color: "rgba(255,255,255,0.6)", marginBottom: 6 }}>Questionnaire C · 12-14 ans</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Quel est le profil de votre enfant ?</div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>Trouvons les activités qui lui correspondent vraiment</div>
      </div>
      <div style={{ padding: 16 }}>
        <div style={card}>
          <div style={{ ...sm, marginBottom: 16 }}>Avant de commencer</div>
          <div style={{ fontSize: 14, color: "#1A3A4A", lineHeight: 1.7, marginBottom: 16 }}>
            À 12-14 ans, ton enfant est le meilleur expert de lui-même. Ce questionnaire se remplit en <b>deux temps</b> :<br />
            <span style={{ display: "flex", gap: 10, alignItems: "center", margin: "8px 0" }}>
              <span style={{ background: VP, color: V, borderRadius: 100, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>1</span>
              <span><b>L'adolescent répond seul</b> à 15 questions (15 min)</span>
            </span>
            <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ background: VP, color: V, borderRadius: 100, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>2</span>
              <span><b>Les parents répondent seuls</b> à 5 questions courtes (5 min)</span>
            </span>
          </div>
          <div style={{ background: "#FDF6E3", borderLeft: `4px solid ${G}`, padding: "12px 14px", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#6B5000", marginBottom: 24 }}>
            Ne montrez pas vos réponses à votre enfant — et réciproquement. Les deux regards indépendants produisent les meilleures recommandations.
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "#4A9BC4", marginBottom: 5 }}>Prénom</div>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Prénom de l'adolescent..." style={{ width: "100%", padding: "10px 14px", border: `1.5px solid ${VP}`, borderRadius: 10, fontSize: 15, boxSizing: "border-box", fontFamily: "system-ui" }} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: "#4A9BC4", marginBottom: 5 }}>Âge</div>
            <input value={age} onChange={e => setAge(e.target.value)} placeholder="Ex: 13 ans" style={{ width: "50%", padding: "10px 14px", border: `1.5px solid ${VP}`, borderRadius: 10, fontSize: 15, fontFamily: "system-ui" }} />
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: "#8B7BB5", marginBottom: 8 }}>Votre enfant est...</div>
            <div style={{ display: "flex", gap: 12 }}>
              {["Fille", "Garçon"].map(g => (
                <div key={g} onClick={() => setGender(g)}
                  style={{ flex: 1, padding: "10px", borderRadius: 12, cursor: "pointer", textAlign: "center", border: gender === g ? "2px solid #6B4FBB" : "2px solid #EDE8FF", background: gender === g ? "#EDE8FF" : "white", fontWeight: gender === g ? 700 : 400, color: gender === g ? "#3D2B8E" : "#1A1035", fontSize: 14 }}>
                  {g}
                </div>
              ))}
            </div>
          </div>
          </div>
          <button style={btn()} onClick={() => setStep(1)}>Commencer →</button>
        </div>
      </div>
    </div>
  );

  // QUESTIONS ADO
  if (step === 1) {
    const q = ADO_QUESTIONS[aq];
    const ans = adoAns[q.id] || [];
    const pct = Math.round(((aq + 1) / ADO_QUESTIONS.length) * 100);
    const ok = q.multi ? ans.length > 0 : ans.length === 1;
    return (
      <div style={{ background: "#F0F8FF", minHeight: "100vh", fontFamily: "system-ui" }}>
        <div style={{ background: `linear-gradient(135deg, ${V}, ${VM})`, padding: "20px 20px 16px", borderRadius: "0 0 20px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ ...sm, color: "rgba(255,255,255,0.6)" }}>Pour {name || "l'ado"} · {q.consigne}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{aq + 1}/{ADO_QUESTIONS.length}</div>
          </div>
          <div style={{ height: 5, background: "rgba(255,255,255,0.2)", borderRadius: 4 }}>
            <div style={{ height: "100%", background: G, borderRadius: 4, width: pct + "%", transition: "width 0.3s" }} />
          </div>
        </div>
        <div style={{ padding: 16 }}>
          <div style={card}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#0A2030", marginBottom: 6, lineHeight: 1.4 }}>{applyGenderQ(q.text, gender)}</div>
            <div style={{ fontSize: 12, color: "#4A9BC4", marginBottom: 16, fontStyle: "italic" }}>{q.consigne}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              {q.options.map(o => {
                const sel = ans.includes(o.id);
                return (
                  <div key={o.id} onClick={() => ansAdo(q.id, o.id, q.multi, q.max)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", borderRadius: 12, cursor: "pointer", border: sel ? `2px solid ${VM}` : `2px solid ${VP}`, background: sel ? VP : "#fff", transition: "all 0.15s" }}>
                    <span style={{ width: 20, height: 20, borderRadius: q.multi ? 4 : 100, border: sel ? "none" : `2px solid #9BC4D4`, background: sel ? VM : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, color: "#fff" }}>{sel ? "✓" : ""}</span>
                    <span style={{ fontSize: 14, fontWeight: sel ? 700 : 400, color: sel ? V : "#1A1035" }}>{gender === "Fille" ? (o.textF || o.text) : o.text}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button style={bto} onClick={prevA}>← Retour</button>
              <button style={{ ...btn(), opacity: !ok ? 0.4 : 1 }} onClick={nextA} disabled={!ok}>
                {aq < ADO_QUESTIONS.length - 1 ? "Suivant →" : "Terminer ma partie →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // QUESTIONS PARENT
  if (step === 2) {
    const q = PARENT_QUESTIONS[pq];
    const ans = parAns[q.id] || [];
    const pct = Math.round(((pq + 1) / PARENT_QUESTIONS.length) * 100);
    const ok = q.multi ? ans.length > 0 : ans.length === 1;
    const PC = "#2E7D32", PL = "#E8F5E9";
    return (
      <div style={{ background: "#F0FFF4", minHeight: "100vh", fontFamily: "system-ui" }}>
        <div style={{ background: "linear-gradient(135deg, #1B5E20, #2E7D32)", padding: "20px 20px 16px", borderRadius: "0 0 20px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" }}>Pour les parents · {q.consigne}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{pq + 1}/{PARENT_QUESTIONS.length}</div>
          </div>
          <div style={{ height: 5, background: "rgba(255,255,255,0.2)", borderRadius: 4 }}>
            <div style={{ height: "100%", background: G, borderRadius: 4, width: pct + "%", transition: "width 0.3s" }} />
          </div>
        </div>
        <div style={{ padding: 16 }}>
          <div style={card}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#0A2010", marginBottom: 6, lineHeight: 1.4 }}>{applyGenderQ(q.text, gender)}</div>
            <div style={{ fontSize: 12, color: "#4A9B6C", marginBottom: 16, fontStyle: "italic" }}>{q.consigne}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
              {q.options.map(o => {
                const sel = ans.includes(o.id);
                return (
                  <div key={o.id} onClick={() => ansPar(q.id, o.id, q.multi, q.max)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 12, cursor: "pointer", border: sel ? `2px solid ${PC}` : `2px solid ${PL}`, background: sel ? PL : "#fff", transition: "all 0.15s" }}>
                    <span style={{ width: 20, height: 20, borderRadius: q.multi ? 4 : 100, border: sel ? "none" : "2px solid #9BC4A0", background: sel ? PC : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, color: "#fff" }}>{sel ? "✓" : ""}</span>
                    <span style={{ fontSize: 14, fontWeight: sel ? 700 : 400, color: sel ? "#1B4A20" : "#1A1035" }}>{gender === "Fille" ? (o.textF || o.text) : o.text}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button style={{ ...bto, borderColor: "#9BC4A0", color: PC }} onClick={prevP}>← Retour</button>
              <button style={{ ...btn(PC), opacity: !ok ? 0.4 : 1 }} onClick={nextP} disabled={!ok}>
                {pq < PARENT_QUESTIONS.length - 1 ? "Suivant →" : "Voir le profil →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // RÉSULTATS
  if (step === 3 && res) {
    const P = PROFILES[res.primary];
    const S = PROFILES[res.secondary];
    const TC = "#1B6B6B";
    return (
      <div style={{ fontFamily: "system-ui", background: "#F5FAFA", minHeight: "100vh" }}>
        <div style={{ background: `linear-gradient(135deg, ${P?.color || TC}, ${S?.color || TC})`, padding: "40px 20px 32px", borderRadius: "0 0 32px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>{P?.emoji}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", marginBottom: 4 }}>{P?.name}</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", marginBottom: 16 }}>Profil principal</div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.15)", borderRadius: 100, padding: "6px 16px" }}>
            <span>{S?.emoji}</span>
            <span style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{S?.name} · profil secondaire</span>
          </div>
        </div>
        <div style={{ padding: "32px 20px", maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: "28px 24px", marginBottom: 20, boxShadow: "0 4px 24px rgba(27,107,107,0.08)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0D2B2B", margin: "0 0 12px" }}>
              Votre rapport personnalisé est prêt
            </h2>
            <p style={{ fontSize: 14, color: "#4A3F72", lineHeight: 1.7, margin: "0 0 24px" }}>
              Le rapport PDF contient le profil complet, les 4 dimensions, les activités recommandées et les conseils aux parents.
            </p>
            <GenerateReport childName={name} childAge={age} gender={gender} primaryKey={res.primary} secondaryKey={res.secondary} scores={res.pct} questionnaire="C" />
          </div>
          <button style={{ background: "transparent", color: TC, border: "2px solid #1B6B6B", borderRadius: 100, padding: "12px 28px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "system-ui", marginTop: 8 }} onClick={onBack || (() => window.location.href = "/")}>
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return null;
}